import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ArrowLeft, ExternalLink } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { contentApi } from '../services/api'
import { ContentPage as ContentPageType } from '../types'
import { Breadcrumbs, generateBreadcrumbs } from '../components/Breadcrumbs'
import { SiteLinks } from '../components/SiteLinks'

// Progress bar component
function ReadingProgress() {
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    const updateProgress = () => {
      const scrollTop = window.scrollY
      const docHeight = document.documentElement.scrollHeight - window.innerHeight
      const scrollPercent = (scrollTop / docHeight) * 100
      setProgress(Math.min(100, Math.max(0, scrollPercent)))
    }

    window.addEventListener('scroll', updateProgress)
    updateProgress() // Initial calculation

    return () => window.removeEventListener('scroll', updateProgress)
  }, [])

  return (
    <div className="fixed top-0 left-0 w-full h-1 bg-gray-200 z-50">
      <div
        className="h-full bg-gradient-to-r from-primary-500 to-primary-600 transition-all duration-150 ease-out"
        style={{ width: `${progress}%` }}
      />
    </div>
  )
}

interface ContentPageProps {
  pageSlug: string
}

export function ContentPage({ pageSlug }: ContentPageProps) {
  const location = useLocation()
  const [page, setPage] = useState<ContentPageType | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true)
        setError(null)

        const response = await contentApi.getPage(pageSlug)

        if (response.success && response.data) {
          setPage(response.data.page)

          // Update page title and meta description
          document.title = `${response.data.page.title} | llm.txt Generator`

          const metaDescription = document.querySelector('meta[name="description"]')
          if (metaDescription) {
            metaDescription.setAttribute('content', response.data.page.meta_description)
          }
        } else {
          setError(response.error || 'Page not found')
        }
      } catch (err) {
        setError('An error occurred while loading the page')
      } finally {
        setLoading(false)
      }
    }

    if (pageSlug) {
      fetchPage()
    }
  }, [pageSlug])

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <ReadingProgress />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading page...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !page) {
    return (
      <div className="min-h-screen bg-white">
        <ReadingProgress />
        <div className="flex items-center justify-center min-h-screen px-4">
          <div className="text-center max-w-md">
            <h1 className="text-2xl font-semibold text-red-600 mb-4">
              Page Not Found
            </h1>
            <p className="text-gray-600 mb-6">
              {error || 'The page you are looking for does not exist.'}
            </p>
            <Link to="/" className="btn-primary">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      <ReadingProgress />

      {/* Navigation */}
      <div className="sticky top-1 bg-white/95 backdrop-blur-sm border-b border-gray-100 z-40">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/"
              className="inline-flex items-center text-primary-600 hover:text-primary-700 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>

            {page && (
              <Breadcrumbs
                items={generateBreadcrumbs(location.pathname, page.title)}
                className="hidden md:flex"
              />
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <article className="max-w-5xl mx-auto px-6 py-12">
        <header className="mb-16 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            {page.title}
          </h1>
          {page.description && (
            <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
              {page.description}
            </p>
          )}
        </header>

        <div className="prose prose-xl max-w-none prose-headings:text-gray-900 prose-headings:font-semibold prose-p:text-gray-700 prose-p:leading-relaxed prose-a:text-primary-600 prose-a:no-underline hover:prose-a:underline prose-strong:text-gray-900 prose-code:text-primary-600 prose-code:bg-primary-50 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-pre:bg-gray-50 prose-pre:border prose-blockquote:border-l-primary-500 prose-blockquote:bg-primary-50 prose-blockquote:pl-6 prose-blockquote:py-4 prose-ul:text-gray-700 prose-ol:text-gray-700">
            <ReactMarkdown
              components={{
                h1: ({ children }) => (
                  <h1 className="text-3xl font-bold font-heading text-gray-900 mt-8 mb-4 first:mt-0">
                    {children}
                  </h1>
                ),
                h2: ({ children }) => (
                  <h2 className="text-2xl font-semibold font-heading text-gray-900 mt-8 mb-4">
                    {children}
                  </h2>
                ),
                h3: ({ children }) => (
                  <h3 className="text-xl font-semibold font-heading text-gray-900 mt-6 mb-3">
                    {children}
                  </h3>
                ),
                p: ({ children }) => (
                  <p className="text-gray-700 leading-relaxed mb-4">
                    {children}
                  </p>
                ),
                ul: ({ children }) => (
                  <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                    {children}
                  </ul>
                ),
                ol: ({ children }) => (
                  <ol className="list-decimal list-inside text-gray-700 mb-4 space-y-2">
                    {children}
                  </ol>
                ),
                blockquote: ({ children }) => (
                  <blockquote className="border-l-4 border-primary-500 pl-4 italic text-gray-600 my-6">
                    {children}
                  </blockquote>
                ),
                code: ({ children }) => (
                  <code className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-sm">
                    {children}
                  </code>
                ),
                pre: ({ children }) => (
                  <pre className="bg-gray-100 text-gray-800 p-4 rounded-lg overflow-x-auto mb-4">
                    {children}
                  </pre>
                ),
                a: ({ href, children }) => (
                  <a
                    href={href}
                    className="text-primary-600 hover:text-primary-700 underline inline-flex items-center"
                    target={href?.startsWith('http') ? '_blank' : undefined}
                    rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
                  >
                    {children}
                    {href?.startsWith('http') && (
                      <ExternalLink className="h-3 w-3 ml-1" />
                    )}
                  </a>
                ),
                strong: ({ children }) => (
                  <strong className="font-semibold text-gray-900">
                    {children}
                  </strong>
                ),
              }}
            >
              {page.content}
            </ReactMarkdown>
          </div>

          {/* Call to Action */}
          <footer className="mt-12 pt-8 border-t border-gray-200">
            <div className="text-center">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Ready to create your llm.txt file?
              </h3>
              <p className="text-gray-600 mb-6">
                Use our free generator to create a properly formatted llm.txt file for your website.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/generator" className="btn-primary">
                  Create Your llm.txt
                </Link>
                <Link to="/verification" className="btn-outline">
                  Verify Existing File
                </Link>
              </div>
            </div>
          </footer>

        {/* Related Pages */}
        <div className="mt-12">
          <div className="card">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">
              Related Resources
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Link
                to="/what-is-llm-txt"
                className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors duration-200"
              >
                <h4 className="font-medium text-gray-900 mb-2">What is llm.txt?</h4>
                <p className="text-sm text-gray-600">Learn the basics of llm.txt files and how they work.</p>
              </Link>

              <Link
                to="/benefits"
                className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors duration-200"
              >
                <h4 className="font-medium text-gray-900 mb-2">Benefits of llm.txt</h4>
                <p className="text-sm text-gray-600">Discover why your website needs an llm.txt file.</p>
              </Link>

              <Link
                to="/ai-seo-best-practices"
                className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors duration-200"
              >
                <h4 className="font-medium text-gray-900 mb-2">AI SEO Best Practices</h4>
                <p className="text-sm text-gray-600">Complete guide to optimizing for AI tools.</p>
              </Link>

              <Link
                to="/implementation-guide"
                className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors duration-200"
              >
                <h4 className="font-medium text-gray-900 mb-2">Implementation Guide</h4>
                <p className="text-sm text-gray-600">Step-by-step instructions for uploading your file.</p>
              </Link>
            </div>
          </div>
        </div>

        {/* Related Resources */}
        <div className="mt-16">
          <SiteLinks variant="inline" />
        </div>
      </article>
    </div>
  )
}
